import React, { Component } from "react";
import ActiveChaptersPanel from "./chaptersContainer/ActiveChaptersPanel";
import DraftChaptersPanel from "./chaptersContainer/DraftChaptersPanel";
import ArchivedChaptersPanel from "./chaptersContainer/ArchivedChaptersPanel";
import { urlMap, getAuthHeaders } from "../../../utils";
import { AppContext } from "../../../context/AppContext";

class Chapters extends Component {
  static contextType = AppContext;
  state = {
    chapters: [],
    chaptersLoading: false,
    chaptersError: null,

    creatingChapter: false,
    deletingChapterId: null,

    submissionsCountByChapter: {},
  };

  componentDidMount() {
    const pid = this.props.project?.id;
    // If chapters were preloaded via props, use them and skip fetching
    if (Array.isArray(this.props.chapters)) {
      const chapters = this.props.chapters;
      this.setState({ chapters, chaptersLoading: false, chaptersError: null }, () => {
        this.syncContextChapters(chapters);
        this.reportCount(chapters.filter((c) => this.chapterStatusOf(c) === "active"));
      });
      // Load summaries even when chapters are preloaded
      if (pid) {
        this.loadSummaries(pid);
      }
    } else if (pid) {
      this.loadChapters(pid);
    }
    this.loadSubmissionCounts();
  }

  chapterStatusOf = (chapter) => chapter?.status || (chapter?.archived_at ? "archived" : "active");

  componentDidUpdate(prevProps) {
    const prevId = prevProps.project?.id;
    const currId = this.props.project?.id;
    
    // If refreshTrigger changed, reload chapters and summaries
    if (this.props.refreshTrigger !== prevProps.refreshTrigger && currId) {
      console.log('[ChaptersContainer] Refresh trigger changed, reloading data');
      this.loadChapters(currId);
      return;
    }
    
    // If parent sends chapters prop and it changed, adopt it
    if (this.props.chapters !== prevProps.chapters && Array.isArray(this.props.chapters)) {
      const chapters = this.props.chapters;
      this.setState({ chapters, chaptersLoading: false, chaptersError: null }, () => {
        this.syncContextChapters(chapters);
        this.reportCount(chapters.filter((c) => this.chapterStatusOf(c) === "active"));
      });
      // Load summaries when chapters prop changes
      if (currId) {
        this.loadSummaries(currId);
      }
      return;
    }

    // Otherwise, on project change (and no preloaded chapters), fetch
    if (prevId !== currId && currId && !Array.isArray(this.props.chapters)) {
      this.loadChapters(currId);
    }
  }

  reportCount = (chapters) => {
    const n = Array.isArray(chapters) ? chapters.length : 0;
    if (typeof this.props.onCountChange === "function") {
      this.props.onCountChange(n);
    }
  };

  // Keep AppContext.chaptersByProject and currentChapters in sync
  syncContextChapters = (chapters) => {
    const pid = this.props.project?.id;
    if (!pid) return;
    try {
      this.context?.setChapters?.(pid, Array.isArray(chapters) ? chapters : []);
    } catch (_) {
      // no-op if context not available
    }
  };

  loadChapters = async (projectId) => {
    this.setState({ chaptersLoading: true, chaptersError: null });
    try {
      const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
      const url = urlMap.listChapters(projectId);

      const res = await fetch(url, { method: "GET", headers, credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const chapters = await res.json();
      this.setState({ chapters, chaptersLoading: false }, () => {
        this.syncContextChapters(chapters);
        this.reportCount(chapters.filter((c) => this.chapterStatusOf(c) === "active"));
      });
      
      // Load summaries after chapters are loaded
      this.loadSummaries(projectId);
    } catch (err) {
      this.setState(
        {
          chaptersError: err.message || "Failed to load",
          chaptersLoading: false,
          chapters: [],
        },
        () => this.reportCount([])
      );
    }
  };

  loadSummaries = async (projectId) => {
    try {
      const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
      const url = urlMap.getProjectSummaries(projectId);

      const res = await fetch(url, { method: "GET", headers, credentials: "include" });
      if (!res.ok) {
        // Silently fail - summaries are optional
        console.warn(`Failed to load summaries: HTTP ${res.status}`);
        return;
      }

      const summaries = await res.json();
      if (!Array.isArray(summaries)) {
        console.warn('Summaries response is not an array');
        return;
      }

      // Transform summaries into the format expected by context
      const summariesData = summaries.map(s => ({
        chapterId: s.chapter_id,
        summaryId: s.id,
        provider: s.provider,
        model: s.model,
        orderNo: s.order_no,
        success: true,
      }));

      console.log('Loaded summaries:', summariesData.length, 'summaries for project', projectId);
      
      // Store in context
      this.context?.setChapterSummaries?.(projectId, summariesData);
    } catch (err) {
      console.warn('Failed to load summaries:', err);
      // Don't show error to user - summaries are optional
    }
  };

  loadSubmissionCounts = async () => {
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const res = await fetch(urlMap.draftInboxByChapter, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (!res.ok) return; // silently ignore — feature is optional
      const counts = await res.json();
      if (counts && typeof counts === 'object') {
        this.setState({ submissionsCountByChapter: counts });
      }
    } catch (err) {
      console.warn('Failed to load submission counts:', err);
    }
  };

  getAllChapters = () => {
    return Array.isArray(this.state.chapters) ? this.state.chapters : [];
  };

  createChapter = async () => {
    const projectId = this.props.project?.id;
    if (!projectId) return;

    this.setState({ creatingChapter: true });
    try {
      const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
      const res = await fetch(urlMap?.chapterEndpoint, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          projectId,
          title: "Untitled Chapter",
          content: "What is this chapter about in one word?",
        }),
      });

      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }

      const chapter = await res.json();
      this.setState(
        (prev) => {
          const chapters = [...prev.chapters, chapter].sort(
            (a, b) =>
              (a.order_no ?? 0) - (b.order_no ?? 0) ||
              new Date(a.created_at) - new Date(b.created_at)
          );
          return { chapters };
        },
        () => {
          this.syncContextChapters(this.state.chapters);
          this.reportCount(this.state.chapters.filter((c) => this.chapterStatusOf(c) === "active"));
          this.props.navigate(`/project/${projectId}/chapter/${chapter.id}`);
        }
      );
    } catch (e) {
      alert(`Could not create chapter: ${e.message}`);
    } finally {
      this.setState({ creatingChapter: false });
    }
  };

  deleteChapter = async (chapterId, title = "this chapter") => {
  // Confirm before proceeding
  const ok = window.confirm(`Delete “${title}”? This cannot be undone.`);
  if (!ok) return;

  this.setState({ deletingChapterId: chapterId });
  try {
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
    const res = await fetch(urlMap.deleteChapter(chapterId), {
      method: "DELETE",
      headers,
      credentials: "include",
    });

    if (res.status === 204) {
      this.setState(
        (prev) => {
          const chapters = prev.chapters.filter((c) => c.id !== chapterId);
          return { chapters };
        },
        () => {
          this.syncContextChapters(this.state.chapters);
          this.reportCount(this.state.chapters.filter((c) => this.chapterStatusOf(c) === "active"));
        }
      );
    } else {
      const payload = await res.json().catch(() => ({}));
      const msg = payload?.error || `HTTP_${res.status}`;
      alert(`Could not delete chapter: ${msg}`);
    }
  } catch (e) {
    alert(`Could not delete chapter: ${e.message}`);
  } finally {
    this.setState({ deletingChapterId: null });
  }
};


  moveChapterToAct = async (chapterId, actKey) => {
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
    const res = await fetch(`${urlMap.putChapterAct(chapterId)}`, {
      method: "PUT",
      headers,
      credentials: "include",
      body: JSON.stringify({ act_key: actKey }),
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      const msg = payload?.error || `HTTP_${res.status}`;
      throw new Error(msg);
    }

    const updated = await res.json();
    this.setState((prev) => ({
      chapters: prev.chapters.map((c) =>
        c.id === chapterId ? { ...c, act_key: updated.act_key } : c
      ),
    }), () => {
      this.syncContextChapters(this.state.chapters);
    });
    return updated;
  };

  moveChapter = async ({ chapterId, targetActKey, position, targetChapterId }) => {
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
    const res = await fetch(`/core/chapters/${chapterId}/move`, {
      method: "PUT",
      headers,
      credentials: "include",
      body: JSON.stringify({
        target_act_key: targetActKey ?? null,
        position,
        target_chapter_id: targetChapterId ?? null,
      }),
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      throw new Error(payload?.error || `HTTP_${res.status}`);
    }

    const data = await res.json();
    const next = Array.isArray(data) ? data : data?.chapters || [];
    if (Array.isArray(next) && next.length >= 0) {
      this.setState({ chapters: next }, () => {
        this.syncContextChapters(next);
        this.reportCount(next.filter((c) => this.chapterStatusOf(c) === "active"));
      });
    } else {
      await this.loadChapters(this.props.project.id);
    }
  };

  setChapterStatus = async (chapterId, status, note = "") => {
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
    const res = await fetch(urlMap.updateChapterStatus(chapterId), {
      method: "PATCH",
      headers,
      credentials: "include",
      body: JSON.stringify({ status, note }),
    });

    if (!res.ok) {
      throw new Error((await res.json().catch(() => ({})))?.error || `HTTP_${res.status}`);
    }

    const updated = await res.json();
    this.setState(
      (prev) => ({
        chapters: prev.chapters
          .map((c) => (c.id === updated.id ? updated : c))
          .sort((a, b) => (a.order_no ?? 0) - (b.order_no ?? 0) || new Date(a.created_at) - new Date(b.created_at)),
      }),
      () => {
        this.syncContextChapters(this.state.chapters);
        this.reportCount(this.state.chapters.filter((c) => this.chapterStatusOf(c) === "active"));
      }
    );

    return updated;
  };

  archiveChapter = async (chapterId, note = "") => this.setChapterStatus(chapterId, "archived", note);

  restoreChapter = async (chapterId) => this.setChapterStatus(chapterId, "active");

  moveChapterToDraft = async (chapterId) => this.setChapterStatus(chapterId, "draft");

  openDraftChapter = (chapter) => {
    const chapterId = chapter?.id;
    const projectId = chapter?.project_id || this.props.project?.id;
    if (!chapterId || !projectId || !this.props.navigate) return;
    this.props.navigate(`/project/${projectId}/chapter/${chapterId}`);
  };


  render() {
    const {
      chapters,
      chaptersLoading,
      chaptersError,
      creatingChapter,
      deletingChapterId,
    } = this.state;

    const {
      project,
      projectType,
      projectTypeLoading,
      projectTypeError,
      readOnly,
      navigate,
      onClose,
    } = this.props;

    if (!project) return null;

    // Get summaries from context
    const projectId = project?.id;
    const summariesList = this.context?.state?.chapterSummariesByProject?.[projectId] || [];
    
    // Create a map: { chapterId: summaryData }
    const summariesMap = {};
    summariesList.forEach(item => {
      if (item.chapterId) {
        summariesMap[item.chapterId] = {
          summaryId: item.summaryId,
          provider: item.provider,
          model: item.model,
          orderNo: item.orderNo,
          success: item.success,
        };
      }
    });
    
    console.log('Render: summariesList =', summariesList.length, 'summariesMap keys =', Object.keys(summariesMap).length);

    const combinedLoading = chaptersLoading || projectTypeLoading;
    const combinedError = chaptersError || projectTypeError;

    // Split into active / draft / archived
    const activeChapters = chapters.filter((c) => this.chapterStatusOf(c) === "active");
    const draftChapters = chapters.filter((c) => this.chapterStatusOf(c) === "draft");
    const archivedChapters = chapters.filter((c) => this.chapterStatusOf(c) === "archived");

    const commonPanelProps = {
      project,
      loading: combinedLoading,
      error: combinedError,
      readOnly,
      navigate,
      onClose,
      onCreateChapter: this.createChapter,
      onDeleteChapter: this.deleteChapter,
      creatingChapter,
      deletingChapterId,
      onArchiveChapter: this.archiveChapter,
      onRestoreChapter: this.restoreChapter,
      onSetChapterStatus: this.setChapterStatus,
      summariesMap, // Pass summaries map to child components
      submissionsCountByChapter: this.state.submissionsCountByChapter,
      onReloadSubmissions: this.loadSubmissionCounts,
    };

    return (
      <>
        {/* Short story: single item — no tabs, no list */}
        {project.project_type_key === 'short_story' ? (
          <ul className="chapters-list">
            {combinedLoading ? (
              <li className="chapter-row is-skeleton">
                <div className="chapter-index">1</div>
                <div className="chapter-main"><div className="chapter-title">Loading…</div></div>
              </li>
            ) : combinedError ? (
              <li className="chapter-row is-static"><div className="chapter-main chapter-title">Could not load: {combinedError}</div></li>
            ) : (() => {
              const story = chapters[0];
              if (!story) {
                return (
                  <li className="chapter-row is-static">
                    <div className="chapter-index">–</div>
                    <div className="chapter-main"><div className="chapter-title">No story yet</div></div>
                  </li>
                );
              }
              return (
                <li
                  className="chapter-row"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/project/${project.id}/chapter/${story.id}`)}
                >
                  <div className="chapter-index">1</div>
                  <div className="chapter-main">
                    <div className="chapter-title-row">
                      <span className="chapter-title-text">{story.title}</span>
                    </div>
                  </div>
                  <div className="chapter-row-actions">
                    <button
                      type="button"
                      className="btn subtle"
                      onClick={(e) => { e.stopPropagation(); navigate(`/project/${project.id}/chapter/${story.id}`); }}
                    >
                      Open
                    </button>
                  </div>
                </li>
              );
            })()}
          </ul>
        ) : (
          <>
        <ActiveChaptersPanel
          {...commonPanelProps}
          chapters={activeChapters}
          projectType={projectType}
          onChangeChapterAct={this.moveChapterToAct}
          onMoveChapter={this.moveChapter}
        />

        <DraftChaptersPanel
          project={project}
          chapters={draftChapters}
          loading={combinedLoading}
          error={combinedError}
          onSetChapterStatus={this.setChapterStatus}
          onDropToDraft={this.moveChapterToDraft}
          onOpenChapter={this.openDraftChapter}
          onDeleteChapter={this.deleteChapter}
          summariesMap={summariesMap}
        />

        <ArchivedChaptersPanel
          project={project}
          chapters={archivedChapters}
          loading={combinedLoading}
          error={combinedError}
          onSetChapterStatus={this.setChapterStatus}
          onDeleteChapter={this.deleteChapter}
          summariesMap={summariesMap}
        />
          </>
        )}
      </>
    );
  }
}

export default Chapters;
